import {
  Box,
  Grid,
  makeStyles,
  Typography,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
} from '@material-ui/core';
import { Button, StandardDialog, SelectField } from '@roc/ui';
import { useStore } from '@roc/client-portal-shared/hooks';
import { observer } from 'mobx-react';
import { useState, useEffect } from 'react';
import { PhotoCover } from '@roc/ui';
import { formatDate } from '@roc/feature-utils';

const useStyles = makeStyles(theme => ({
  photoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gap: '16px',
    marginTop: '16px',
  },
  photoItem: {
    position: 'relative',
    cursor: 'pointer',
    border: '2px solid',
    borderRadius: '8px',
    overflow: 'hidden',
    transition: 'all 0.2s ease',
  },
  photoItemSelected: {
    borderColor: theme.palette.primary.main,
    boxShadow: '0 0 8px rgba(57, 188, 208, 0.5)',
  },
  photoItemUnselected: {
    borderColor: '#e0e0e0',
  },
  checkbox: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    backgroundColor: 'white',
    borderRadius: '4px',
    padding: '2px',
    zIndex: 1,
  },
  photoInfo: {
    padding: '4px 8px',
    fontSize: '12px',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
  },
  section: {
    marginTop: '24px',
  },
  selectAllButton: {
    marginTop: '8px',
  },
  actionSection: {
    marginTop: '24px',
    padding: '16px',
    backgroundColor: 'transparent',
    borderRadius: '8px',
  },
  selectionFields: {
    marginTop: '16px',
  },
}));

interface MovePhotosDialogProps {
  open: boolean;
  onClose: () => void;
}

export const MovePhotosDialog = observer(
  ({ open, onClose }: MovePhotosDialogProps) => {
    const classes = useStyles();
    const { inspectionStore, globalStore } = useStore();
    const { inspectionPhotosStore } = inspectionStore;
    const inspectionPhotos = inspectionPhotosStore.inspectionPhotos || [];
    const inspectionDetails = inspectionStore.inspectionDetails;

    const [selectedPhotoIds, setSelectedPhotoIds] = useState<Set<number>>(
      new Set()
    );
    const [actionType, setActionType] = useState<'copy' | 'move'>('copy');
    const [selectedProperty, setSelectedProperty] = useState('');
    const [selectedDraw, setSelectedDraw] = useState('');

    useEffect(() => {
      if (open) {
        setSelectedPhotoIds(new Set());
        setActionType('copy');
        setSelectedProperty('');
        setSelectedDraw('');
        inspectionPhotosStore.resetMoveData();
        if (inspectionDetails?.externalId1) {
          inspectionPhotosStore.fetchPropertiesForMove(
            inspectionDetails.externalId1
          );
        }
      }
    }, [open]);

    useEffect(() => {
      if (selectedProperty && inspectionDetails?.externalId1) {
        inspectionPhotosStore.fetchDrawsForMove(
          inspectionDetails.externalId1,
          selectedProperty
        );
      } else {
        inspectionPhotosStore.clearDraws();
        setSelectedDraw('');
      }
    }, [selectedProperty]);

    const handlePhotoToggle = (photoId: number) => {
      const newSelected = new Set(selectedPhotoIds);
      if (newSelected.has(photoId)) {
        newSelected.delete(photoId);
      } else {
        newSelected.add(photoId);
      }
      setSelectedPhotoIds(newSelected);
    };

    const handleSelectAll = () => {
      if (selectedPhotoIds.size === inspectionPhotos.length) {
        setSelectedPhotoIds(new Set());
      } else {
        const allIds = new Set(
          inspectionPhotos.map(photo => photo.inspectionPhotoId)
        );
        setSelectedPhotoIds(allIds);
      }
    };

    const handleActionTypeChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      setActionType(event.target.value as 'copy' | 'move');
    };

    const handleClose = () => {
      onClose();
    };

    const handleSubmit = async () => {
      if (!inspectionDetails?.inspectionId) return;

      const selectedDrawData = inspectionPhotosStore.draws.find(
        d => d.inspectionId === parseInt(selectedDraw)
      );
      if (!selectedDrawData) return;

      const selectedPhotosArray = Array.from(selectedPhotoIds);

      if (actionType === 'copy') {
        await inspectionPhotosStore.copyPhotos(
          selectedPhotosArray,
          inspectionDetails.inspectionId,
          selectedDrawData.inspectionId
        );
      } else {
        await inspectionPhotosStore.movePhotos(
          selectedPhotosArray,
          inspectionDetails.inspectionId,
          selectedDrawData.inspectionId
        );
      }

      onClose();
    };

    const isAllSelected =
      selectedPhotoIds.size === inspectionPhotos.length &&
      inspectionPhotos.length > 0;

    const dialogContent = (
      <Box>
        <Typography
          variant="body1"
          color="textSecondary"
          style={{ marginBottom: '16px' }}
        >
          Please select the photographs you wish to move within Draws in this
          loan.
        </Typography>

        <Button
          variant="outlined"
          color="primary"
          onClick={handleSelectAll}
          className={classes.selectAllButton}
          fullWidth
          testId="select-all-photos"
        >
          {isAllSelected ? 'DESELECT ALL PHOTOS' : 'SELECT ALL PHOTOS'}
        </Button>

        <div className={classes.photoGrid}>
          {inspectionPhotos.map(photo => {
            const isSelected = selectedPhotoIds.has(photo.inspectionPhotoId);
            return (
              <div
                key={photo.inspectionPhotoId}
                className={`${classes.photoItem} ${
                  isSelected
                    ? classes.photoItemSelected
                    : classes.photoItemUnselected
                }`}
                onClick={() => handlePhotoToggle(photo.inspectionPhotoId)}
              >
                <Checkbox
                  checked={isSelected}
                  className={classes.checkbox}
                  color="primary"
                  onClick={e => {
                    e.stopPropagation();
                    handlePhotoToggle(photo.inspectionPhotoId);
                  }}
                />
                <PhotoCover
                  url={photo.thumbnailImage || photo.image}
                  rotationDegrees={photo.preferredRotation || 0}
                  width="100%"
                  height="120px"
                  caption={photo.caption}
                />
                <Box className={classes.photoInfo}>
                  <Typography variant="caption">
                    {formatDate(photo.captureTimeIso, "MM-dd-yyyy hh:mm:ss aaa")}
                  </Typography>
                </Box>
              </div>
            );
          })}
        </div>

        <Box className={classes.actionSection}>
          <FormControl component="fieldset">
            <FormLabel component="legend">
              <Typography variant="h6">
                What do you want to do with these photos?
              </Typography>
            </FormLabel>
            <RadioGroup
              value={actionType}
              onChange={handleActionTypeChange}
              style={{ marginTop: '12px' }}
            >
              <FormControlLabel
                value="copy"
                control={<Radio color="primary" />}
                label="Copy to Another Draw"
              />
              <FormControlLabel
                value="move"
                control={<Radio color="primary" />}
                label="Move them to Another Draw"
              />
            </RadioGroup>
          </FormControl>

          <Grid container spacing={2} className={classes.selectionFields}>
            <Grid item xs={12}>
              <SelectField
                label="Select Property"
                options={inspectionPhotosStore.properties.map(prop => ({
                  label: prop.fullAddress || `Property ${prop.propertyId}`,
                  value: prop.propertyId.toString(),
                }))}
                value={selectedProperty}
                onChange={value => setSelectedProperty(value)}
                fullWidth
                variant="outlined"
                testId="select-property"
                disabled={
                  inspectionPhotosStore.isLoadingMoveData ||
                  inspectionPhotosStore.properties.length === 0
                }
              />
            </Grid>
            <Grid item xs={12}>
              <SelectField
                label="Select the draw for these photos"
                options={inspectionPhotosStore.draws.map(draw => {
                  const isCurrent = draw.inspectionId === inspectionDetails?.inspectionId;
                  return {
                    label: `Draw ${draw.drawNumber || draw.drawId}${isCurrent ? ' (Current)' : ''}`,
                    value: draw.inspectionId.toString(),
                    disabled: isCurrent,
                  };
                })}
                value={selectedDraw}
                onChange={value => setSelectedDraw(value)}
                fullWidth
                variant="outlined"
                testId="select-draw"
                disabled={
                  inspectionPhotosStore.isLoadingMoveData ||
                  !selectedProperty ||
                  inspectionPhotosStore.draws.length === 0
                }
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    );

    const dialogActions = (
      <>
        <Button
          variant="outlined"
          onClick={handleClose}
          testId="cancel-move-photos"
          disabled={inspectionPhotosStore.isLoadingMoveData}
          style={{ marginRight: '8px' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={
            inspectionPhotosStore.isLoadingMoveData ||
            selectedPhotoIds.size === 0 ||
            !selectedProperty ||
            !selectedDraw
          }
          testId="submit-move-photos"
        >
          {actionType === 'copy' ? 'Copy Photos' : 'Move Photos'}
        </Button>
      </>
    );

    return (
      <StandardDialog
        open={open}
        handleClose={handleClose}
        title={`Move Photos`}
        dialogContent={dialogContent}
        dialogActions={dialogActions}
        maxWidth="sm"
        fullWidth
        removeContentBox
      />
    );
  }
);
